import * as React from "react";
import { cn } from "../_internal/cn";
import type { TypePullQuoteProps } from "./PullQuoteTypes";

/**
 * PullQuote — a narrative primitive that spotlights a quotation on an AI
 * gradient surface. The quote sits behind an accented vertical bar with an
 * optional attribution row (author name, avatar, network).
 *
 * Rendered as semantic `<figure>` / `<blockquote>` / `<figcaption>` markup.
 * Styled via `pull-quote.css` and `ai-container.css` (Seeds CSS custom
 * properties); consumers import the classes through
 * `@sproutsocial/racine/css/components`. Surface overrides are done with
 * standard `className` / `style`.
 */
const PullQuote = React.forwardRef<HTMLElement, TypePullQuoteProps>(
  ({ quote, attribution, appearance = "subtle", className, ...props }, ref) => {
    return (
      <figure
        ref={ref}
        className={cn(
          "seeds-pull-quote",
          "seeds-ai-container",
          { "seeds-ai-container-vivid": appearance === "vivid" },
          { "seeds-pull-quote-vivid": appearance === "vivid" },
          className
        )}
        {...props}
      >
        <div className="seeds-pull-quote-bar">
          <blockquote className="seeds-pull-quote-text">{quote}</blockquote>
          {attribution != null && (
            <figcaption className="seeds-pull-quote-attribution">
              {attribution}
            </figcaption>
          )}
        </div>
      </figure>
    );
  }
);

PullQuote.displayName = "PullQuote";

export default PullQuote;
